// La tabla de Outbound Loads: cabecera fija de verdad + una banda por
// proximidad de fecha.
//
// LA CABECERA LLEVA LOS FILTROS DENTRO (`CabeceraFiltrable`), y eso obliga a
// que se quede quieta: una cabecera que se va al desplazar dejaría la pantalla
// sin forma de filtrar. `sticky` sólo funciona dentro del contenedor que
// SCROLLEA, así que el envoltorio pasó a tener alto acotado y a desplazar la
// tabla por dentro — el mismo montaje que `ReportTable`. Antes decía «cabecera
// fija» en este comentario y no lo era: no había un solo `sticky` en la
// carpeta. Sin `Pagination`: la agrupación por fecha YA es la forma de no
// enseñar 557 filas de golpe, y una paginación por encima cortaría una banda
// a la mitad sin que nadie lo entienda.
import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { CabeceraMarca } from '@/components/common/MarcasFilas'
import { CabeceraFiltrable } from '@/components/common/CabeceraFiltrable'
import type { FiltroColumna } from '@/utils/filtroColumna'
import { Loader } from '@/components/common/Loader'
import { EmptyState } from '@/components/common/EmptyState'
import type { GrupoFecha } from '@/utils/agruparPorFecha'
import type { CargaSalida, MarcasFilas } from '@/types'
import { GrupoFechaBanda } from './GrupoFechaBanda'
import { FichaCargaDrawer } from './FichaCargaDrawer'
import type { ColumnaCargaSalida } from './columnasCargaSalida'

interface Props {
  dark: boolean
  grupos: GrupoFecha<CargaSalida>[]
  columnas: ColumnaCargaSalida[]
  /** Lo que ofrece filtrar cada columna, indexado por su `key`. Una columna
   *  que no esté aquí pinta su rótulo a secas. */
  filtros: Record<string, FiltroColumna>
  hoy: Date
  marcas: MarcasFilas
  claveDe: (c: CargaSalida) => string
  isLoading: boolean
  isError: boolean
  hasFilters: boolean
  onClearFilters: () => void
  onRetry: () => void
  /** La ficha pidió ver los back orders: la página cambia de pestaña. */
  onVerBackOrders: () => void
}

export function CargasSalidaTabla({
  dark, grupos, columnas, filtros, hoy, marcas, claveDe, isLoading, isError, hasFilters,
  onClearFilters, onRetry, onVerBackOrders,
}: Props) {
  const { t } = useTranslation()
  const [abiertas, setAbiertas] = useState<Set<string>>(new Set())
  // La ficha se guarda por nº de CARGA, no por objeto: la lista se refresca tras
  // cada acción (estado manual, aviso) y un objeto guardado enseñaría datos
  // viejos. Si un filtro saca la carga de la lista con la ficha abierta, se
  // conserva la última versión vista en vez de cerrarla bajo los pies del usuario.
  // `?ficha=<carga>` la abre al llegar: es como enlaza la situación de ventas.
  // Se lee una vez; el efecto de URL de la página lo quita después.
  const [fichaCarga, setFichaCarga] = useState<string | null>(
    () => new URLSearchParams(window.location.search).get('ficha'),
  )
  const ultimaFicha = useRef<CargaSalida | null>(null)

  function toggleFila(carga: string) {
    setAbiertas(prev => {
      const next = new Set(prev)
      if (next.has(carga)) next.delete(carga); else next.add(carga)
      return next
    })
  }

  const viva = fichaCarga
    ? grupos.flatMap(g => g.items).find(c => c.carga === fichaCarga)
    : undefined
  if (viva) ultimaFicha.current = viva
  const fichaMostrada = fichaCarga ? (viva ?? ultimaFicha.current) : null

  const cardCls = dark ? 'bg-slate-900 border-white/8' : 'bg-white border-slate-200'
  // `truncate` y no sólo `whitespace-nowrap`: con `table-fixed`, una cabecera
  // más larga que su columna NO se recorta sola, se DESBORDA encima de la de
  // al lado. Pasó con «Grupo clasificación» sobre «Incoterm». El `title` del
  // `th` deja el rótulo entero a un palmo del ratón.
  const thBase = 'px-2 py-2 text-left text-[10px] font-bold uppercase tracking-wider truncate'
  // Fondo propio y `z`: una celda fija sin fondo deja ver las filas por debajo
  // al pasar. Y el borde inferior va en la CELDA, no en el `<thead>`: el que
  // está fijo es cada `th`, así que un borde puesto en el `thead` se iría al
  // desplazar y la cabecera quedaría flotando sin línea.
  const thFijo = `sticky top-0 z-20 border-b ${
    dark ? 'bg-slate-900 border-white/8' : 'bg-white border-slate-200'
  }`
  const thCls = `${thBase} ${thFijo} ${dark ? 'text-slate-500' : 'text-slate-600'}`

  if (isLoading) {
    return (
      <div className={`rounded-xl border py-16 ${cardCls}`}>
        <Loader label={t('cargasSalida.cargando')} />
      </div>
    )
  }

  if (isError) {
    return (
      <div className={`rounded-xl border ${cardCls}`}>
        <EmptyState
          dark={dark}
          title={t('common.error')}
          description={t('cargasSalida.error')}
          action={(
            <button
              type="button"
              onClick={onRetry}
              className="text-[12px] font-semibold text-sky-600 hover:text-sky-500 cursor-pointer hover:underline"
            >
              {t('common.refresh')}
            </button>
          )}
        />
      </div>
    )
  }

  const totalCargas = grupos.reduce((n, g) => n + g.items.length, 0)

  if (totalCargas === 0) {
    return (
      <div className={`rounded-xl border ${cardCls}`}>
        <EmptyState
          dark={dark}
          title={t(hasFilters ? 'cargasSalida.vacio.filtrosTitulo' : 'cargasSalida.vacio.titulo')}
          description={t(hasFilters ? 'cargasSalida.vacio.filtrosDescripcion' : 'cargasSalida.vacio.descripcion')}
          action={hasFilters ? (
            <button
              type="button"
              onClick={onClearFilters}
              className="text-[12px] font-semibold text-sky-600 hover:text-sky-500 cursor-pointer hover:underline"
            >
              {t('common.clearFilters')}
            </button>
          ) : undefined}
        />
      </div>
    )
  }

  /** El rótulo de una columna: con filtro si lo tiene, a secas si no. */
  function rotulo(clave: string, etiqueta: string) {
    const filtro = filtros[clave]
    if (!filtro) return etiqueta
    return <CabeceraFiltrable dark={dark} etiqueta={etiqueta} filtro={filtro} />
  }

  return (
    <>
    {/* Alto acotado para que el scroll vertical sea de ESTE contenedor y la
        cabecera fija tenga de dónde agarrarse. `min-h` para que en una ventana
        baja no se quede en una tabla de tres filas. */}
    <div className={`rounded-xl border overflow-auto scroll-thin max-h-[calc(100vh-22rem)] min-h-[20rem] ${cardCls}`}>
      <table className="w-full table-fixed border-collapse text-left min-w-[1650px]">
        <colgroup>
          <col className="w-6" />
          <col className="w-[170px]" />
          {columnas.map(col => <col key={col.key} className={col.ancho} />)}
        </colgroup>
        <thead>
          <tr>
            <CabeceraMarca className={thFijo} />
            <th className={thCls} title={filtros.carga ? undefined : t('cargasSalida.tabla.columnas.carga')}>
              {rotulo('carga', t('cargasSalida.tabla.columnas.carga'))}
            </th>
            {columnas.map(col => (
              <th
                key={col.key}
                className={`${thCls} ${col.alinear === 'right' ? 'text-right' : ''}`}
                title={filtros[col.key] ? undefined : t(col.etiqueta)}
              >
                {rotulo(col.key, t(col.etiqueta))}
              </th>
            ))}
          </tr>
        </thead>
        {grupos.map(grupo => (
          <GrupoFechaBanda
            key={grupo.bucket}
            grupo={grupo}
            dark={dark}
            columnas={columnas}
            hoy={hoy}
            marcas={marcas}
            claveDe={claveDe}
            abiertaDe={carga => abiertas.has(carga)}
            onToggleFila={toggleFila}
            onFicha={setFichaCarga}
          />
        ))}
      </table>
    </div>

    {fichaMostrada && (
      <FichaCargaDrawer
        c={fichaMostrada}
        dark={dark}
        onClose={() => setFichaCarga(null)}
        onVerBackOrders={onVerBackOrders}
      />
    )}
    </>
  )
}
